import { Badge } from '@thegpvc/ui';
import { SectionCard } from './SectionCard';
import type { FrictionMoment } from '../types/api';

const TYPE_LABEL: Record<string, string> = {
  correction: 'Correction',
  friction: 'Friction',
};
const TYPE_VARIANT: Record<string, 'error' | 'warning' | 'neutral'> = {
  correction: 'warning',
  friction: 'error',
};

/**
 * FrictionCard — one friction Moment rendered as its own subcard within the
 * cream panel. The badge + description form the header row; the verbatim
 * evidence quote (when present) sits below as an italic blockquote. Mirrors the
 * per-item subcard treatment the chapter cards adopted in #601.
 */
function FrictionCard({ moment }: { moment: FrictionMoment }) {
  return (
    <li
      className="space-y-2"
      style={{
        backgroundColor: '#ffffff',
        border: '1px solid #efe9dd',
        borderRadius: 9,
        padding: '10px 12px',
      }}
    >
      <div className="flex items-center gap-2">
        <Badge variant={TYPE_VARIANT[moment.type] ?? 'neutral'}>
          {TYPE_LABEL[moment.type] ?? moment.type}
        </Badge>
        <span className="text-sm" style={{ color: '#06143b' }}>
          {moment.description}
        </span>
      </div>
      {moment.quote && (
        <blockquote
          className="ml-1 border-l-2 pl-3 text-sm italic"
          style={{ borderColor: '#d9d2c4', color: '#667491' }}
        >
          &ldquo;{moment.quote}&rdquo;
        </blockquote>
      )}
    </li>
  );
}

/**
 * SessionFriction surfaces a session's friction Moments — the points where the
 * operator corrected the agent or the agent got stuck/looped — each with its
 * verbatim evidence quote. This is the "how it went" axis (L2), distinct from the
 * coaching "how efficiently it ran" axis (L1). Renders nothing when the session
 * ran without friction.
 *
 * Presentation mirrors the #601 chapter-card treatment: the moments live in a
 * nested cream panel (the "Dixie flagged" surface from FlaggedInsights) with
 * each moment as its own subcard rather than a run-on list.
 */
export function SessionFriction({
  friction,
  heading = 'Friction',
}: {
  friction?: FrictionMoment[];
  heading?: string | null;
}) {
  if (!friction || friction.length === 0) return null;
  const panel = (
    <section
      aria-label="Friction moments"
      style={{
        backgroundColor: '#fbfaf6',
        border: '1px solid #efe9dd',
        borderRadius: 12,
        padding: '14px 16px',
      }}
    >
      <ul className="space-y-2" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
        {friction.map((f, i) => (
          <FrictionCard key={i} moment={f} />
        ))}
      </ul>
    </section>
  );
  if (heading === null) return panel;
  return <SectionCard title={heading}>{panel}</SectionCard>;
}
